import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  JudgeCall,
  Nullable,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

const JUDGE_CALL_FIELDS = gql`
  fragment JudgeCallFields on JudgeCall {
    id
    tournamentId
    matchId
    tableNumber
    callerId
    assignedJudgeId
    status
    priority
    description
    resolution
    createdAt
    resolvedAt
    caller {
      id
      name
      lastName
    }
    assignedJudge {
      id
      name
      lastName
    }
    match {
      id
      round
      tableNumber
    }
  }
`;

export type RequestJudgeCallInput = {
  tournamentId: number;
  matchId?: number;
  tableNumber?: number;
  description?: string;
  priority?: 'NORMAL' | 'URGENT';
};

export const requestJudgeCall = async (
  user: UserWithToken,
  input: RequestJudgeCallInput
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      mutation requestJudgeCall($input: RequestJudgeCallInput!) {
        data: requestJudgeCall(input: $input) {
          ...JudgeCallFields
        }
      }
    `,
    { input }
  );

export const acceptJudgeCall = async (
  user: UserWithToken,
  judgeCallId: number
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      mutation acceptJudgeCall($judgeCallId: Int!) {
        data: acceptJudgeCall(judgeCallId: $judgeCallId) {
          ...JudgeCallFields
        }
      }
    `,
    { judgeCallId }
  );

export const escalateJudgeCall = async (
  user: UserWithToken,
  judgeCallId: number,
  reason?: string
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      mutation escalateJudgeCall($judgeCallId: Int!, $reason: String) {
        data: escalateJudgeCall(judgeCallId: $judgeCallId, reason: $reason) {
          ...JudgeCallFields
        }
      }
    `,
    { judgeCallId, reason: reason ?? null }
  );

export const resolveJudgeCall = async (
  user: UserWithToken,
  judgeCallId: number,
  resolution: string,
  // "Hubo penalización": deja una pendiente en Penalidades para completarla.
  penalty?: { penaltyPending: boolean; penaltyPlayerId?: number | null }
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      mutation resolveJudgeCall(
        $judgeCallId: Int!
        $resolution: String!
        $penaltyPending: Boolean
        $penaltyPlayerId: Int
      ) {
        data: resolveJudgeCall(
          judgeCallId: $judgeCallId
          resolution: $resolution
          penaltyPending: $penaltyPending
          penaltyPlayerId: $penaltyPlayerId
        ) {
          ...JudgeCallFields
        }
      }
    `,
    {
      judgeCallId,
      resolution,
      penaltyPending: penalty?.penaltyPending ?? false,
      penaltyPlayerId: penalty?.penaltyPlayerId ?? null,
    }
  );

export const getActiveJudgeCalls = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall[]>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      query activeJudgeCalls($tournamentId: Int!) {
        data: activeJudgeCalls(tournamentId: $tournamentId) {
          ...JudgeCallFields
        }
      }
    `,
    { tournamentId }
  );

export const getTournamentJudgeCalls = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  status?: 'PENDING' | 'IN_PROGRESS' | 'ESCALATED' | 'RESOLVED'
) =>
  getGraphQLQueryClient(user).request<WithData<JudgeCall[]>>(
    gql`
      ${JUDGE_CALL_FIELDS}
      query tournamentJudgeCalls(
        $tournamentId: Int!
        $status: JudgeCallStatus
      ) {
        data: tournamentJudgeCalls(tournamentId: $tournamentId, status: $status) {
          ...JudgeCallFields
        }
      }
    `,
    { tournamentId, status: status ?? null }
  );
